import { writeWithBackoff } from './firestoreBackoff';
import { db } from '../firebaseConfig';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';

export interface SavePayload {
  projectId: string;
  uid: string;
  cleanProject: any;
  cleanState: any;
  chapterCount: number;
  isCompletionSave: boolean;
}

export class ProjectSaveQueue {
  private isWriting: Record<string, boolean> = {};
  private nextPayload: Record<string, SavePayload | null> = {};
  private currentPromise: Record<string, Promise<boolean> | null> = {};

  async enqueue(payload: SavePayload): Promise<boolean> {
    const { projectId } = payload;
    
    // Check if we already have a pending payload for this project
    const existing = this.nextPayload[projectId];
    
    if (existing) {
      // RULE 1, 2, 4, 5, 6: Merge carefully to avoid stale overwrites
      const isExistingCompletion = existing.isCompletionSave;
      const isNewCompletion = payload.isCompletionSave;
      const existingChapters = existing.chapterCount;
      const newChapters = payload.chapterCount;

      if (isExistingCompletion && !isNewCompletion) {
         // Do not let a smaller/partial save overwrite a pending completion save
         if (import.meta.env.VITE_DEBUG_FIRESTORE === 'true') {
            console.log(`[Firestore Queue] Dropped partial save. Pending save is a completion save.`);
         }
         // We must return the promise of the currently running or queued save.
         return this.currentPromise[projectId] || Promise.resolve(true);
      }

      if (existingChapters > newChapters) {
         // Never allow a stale 19-chapter payload to write after a 20-chapter payload
         if (import.meta.env.VITE_DEBUG_FIRESTORE === 'true') {
            console.log(`[Firestore Queue] Dropped stale save (${newChapters} vs ${existingChapters}).`);
         }
         return this.currentPromise[projectId] || Promise.resolve(true);
      }

      // If we reach here, the new payload supersedes or is equal priority. We replace the pending payload.
      if (import.meta.env.VITE_DEBUG_FIRESTORE === 'true') {
         console.log(`[Firestore Queue] Save collapsed. New payload takes precedence.`);
      }
    }

    this.nextPayload[projectId] = payload;

    if (this.isWriting[projectId]) {
      return this.currentPromise[projectId]!;
    }

    this.isWriting[projectId] = true;
    
    this.currentPromise[projectId] = (async () => {
      let finalSuccess = true;
      while (this.nextPayload[projectId]) {
        const p = this.nextPayload[projectId]!;
        this.nextPayload[projectId] = null; // Clear so new saves can queue during the write
        
        const success = await writeWithBackoff(async () => {
           const { getDoc } = await import('firebase/firestore');
           const projectRef = doc(db, 'users', p.uid, 'projects', p.projectId);
           const existingDoc = await getDoc(projectRef);
           const existingData = existingDoc.data() || {};
           
           const existingFirestoreChapterCount = existingData.chapterCount || 0;
           const incomingChapterCount = p.cleanProject.chapterCount || 0;

           if (incomingChapterCount > 0) {
               if (incomingChapterCount < existingFirestoreChapterCount) {
                   if (import.meta.env.VITE_DEBUG_FIRESTORE === 'true') {
                       console.warn(`[Firestore Queue] Regression protection: stripping downgraded chapterCount (${incomingChapterCount} < ${existingFirestoreChapterCount})`);
                   }
                   delete p.cleanProject.chapterCount;
                   if (p.cleanState?.storyBible) {
                       delete p.cleanState.storyBible.chapterCount;
                   }
               } else {
                   p.cleanProject.chapterCount = Math.max(incomingChapterCount, existingFirestoreChapterCount);
               }
           }

            const isIncomingFree = p.cleanProject.creditTier === 'free_preview' || p.cleanProject.creditTier === 'free' || !p.cleanProject.creditTier;
            
            // Immune to race conditions: if the incoming save thinks it's free, but isn't explicitly downgrading,
            // we NEVER send the free tier to the server. The server's existing tier is preserved.
            if (isIncomingFree && !p.cleanProject.isExplicitDowngrade) {
                delete p.cleanProject.creditTier;
                delete p.cleanProject.maxChaptersAllowed;
                delete p.cleanProject.exportUnlocked;
                delete p.cleanProject.paidEntitlementId;
                delete p.cleanProject.rewriteCreditsIncluded;
                delete p.cleanProject.rewriteCreditsUsed;
                delete p.cleanProject.entitlementAppliedAt;
                
                if (p.cleanState?.storyBible) {
                    delete p.cleanState.storyBible.creditTier;
                    delete p.cleanState.storyBible.maxChaptersAllowed;
                    delete p.cleanState.storyBible.exportUnlocked;
                    delete p.cleanState.storyBible.paidEntitlementId;
                    delete p.cleanState.storyBible.rewriteCreditsIncluded;
                    delete p.cleanState.storyBible.rewriteCreditsUsed;
                    delete p.cleanState.storyBible.entitlementAppliedAt;
                }
                if (import.meta.env.VITE_DEBUG_FIRESTORE === 'true') {
                    console.log(`[Firestore Queue] Prevented stale overwrite of paid entitlement for project ${p.projectId}`);
                }
            }

            // Always cleanup the transient flag
            delete p.cleanProject.isExplicitDowngrade;
            if (p.cleanState?.storyBible) {
                delete p.cleanState.storyBible.isExplicitDowngrade;
            }

           await Promise.all([
              setDoc(projectRef, {
                ...p.cleanProject,
                userId: p.uid,
                updatedAt: serverTimestamp(),
              }, { merge: true }),
              setDoc(doc(db, 'users', p.uid, 'projects', p.projectId, 'state', 'current'), p.cleanState, { merge: true })
           ]);
        });
        
        if (!success) {
           finalSuccess = false;
        }
      }
      this.isWriting[projectId] = false;
      this.currentPromise[projectId] = null;
      return finalSuccess;
    })();

    return this.currentPromise[projectId]!;
  }
}

export const projectSaveQueue = new ProjectSaveQueue();
